import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useState } from 'react';
import {
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

// Context and Utils
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';

type StoryStatus = 'TODO' | 'IN_PROGRESS' | 'TO_REVIEW' | 'DONE';

interface UserStory {
  id: number;
  title: string;
  description: string;
  status: StoryStatus;
  assignedTo: {
    name: string;
    avatar: string;
  };
  assignedBy: {
    name: string;
    avatar: string;
  };
  priority: 'High' | 'Medium' | 'Low';
  storyPoints: number;
}

export default function UserStoriesScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  
  const [filter, setFilter] = useState<StoryStatus | 'ALL'>('ALL');

  const [stories] = useState<UserStory[]>([
    {
      id: 1,
      title: 'Azure Cloud Infrastructure Setup',
      description: 'Implement comprehensive Azure cloud infrastructure for the learning platform',
      status: 'IN_PROGRESS',
      assignedTo: { name: 'Dr. Sarah Chen', avatar: 'SC' },
      assignedBy: { name: 'Prof. Ahmed Hassan', avatar: 'AH' },
      priority: 'High',
      storyPoints: 21
    },
    {
      id: 2,
      title: 'Data Science Learning Path',
      description: 'Create interactive data science curriculum with Python and ML',
      status: 'TODO',
      assignedTo: { name: 'Prof. Ahmed Hassan', avatar: 'AH' },
      assignedBy: { name: 'Dr. Sarah Chen', avatar: 'SC' },
      priority: 'High',
      storyPoints: 18
    },
    {
      id: 3,
      title: 'French Language Module',
      description: 'Develop French language learning content with AI pronunciation',
      status: 'TO_REVIEW',
      assignedTo: { name: 'Marie Dubois', avatar: 'MD' },
      assignedBy: { name: 'Dr. Sarah Chen', avatar: 'SC' },
      priority: 'Medium',
      storyPoints: 13
    },
    {
      id: 4,
      title: 'AWS Cloud Practitioner Course',
      description: 'Build AWS certification preparation materials and labs',
      status: 'DONE',
      assignedTo: { name: 'Tom Brown', avatar: 'TB' },
      assignedBy: { name: 'Prof. Ahmed Hassan', avatar: 'AH' },
      priority: 'High',
      storyPoints: 15
    },
    {
      id: 5,
      title: 'Spanish Business Communication',
      description: 'Create business Spanish course with cultural context',
      status: 'TODO',
      assignedTo: { name: 'Carlos Rodriguez', avatar: 'CR' },
      assignedBy: { name: 'Marie Dubois', avatar: 'MD' },
      priority: 'Medium',
      storyPoints: 12
    },
    {
      id: 6,
      title: 'Machine Learning Fundamentals',
      description: 'Develop ML course with hands-on TensorFlow projects',
      status: 'IN_PROGRESS',
      assignedTo: { name: 'Dr. Sarah Chen', avatar: 'SC' },
      assignedBy: { name: 'Prof. Ahmed Hassan', avatar: 'AH' },
      priority: 'High',
      storyPoints: 25
    }
  ]);

  const filteredStories = filter === 'ALL' 
    ? stories 
    : stories.filter(story => story.status === filter);

  const handleStoryPress = (story: UserStory) => {
    // Navigate to story detail
    navigation.navigate('UserStoryDetail' as never, { 
      missionId: route.params?.missionId || 1, 
      chapterId: route.params?.chapterId,
      storyId: story.id 
    } as never);
  };

  const getStatusColor = (status: StoryStatus) => {
    switch (status) {
      case 'TODO': return colors.warning;
      case 'IN_PROGRESS': return colors.primary;
      case 'TO_REVIEW': return colors.info;
      case 'DONE': return colors.success;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return colors.error;
      case 'Medium': return colors.warning;
      case 'Low': return colors.success;
      default: return colors.textSecondary;
    }
  };

  const renderFilterTab = (status: StoryStatus | 'ALL', label: string, count: number) => (
    <TouchableOpacity
      key={status}
      style={[
        styles.filterTab,
        { backgroundColor: filter === status ? colors.primary : colors.surfaceSecondary }
      ]}
      onPress={() => setFilter(status)}
    >
      <Text style={[
        styles.filterTabText,
        { color: filter === status ? colors.badgeText : colors.textSecondary }
      ]}>
        {label} ({count})
      </Text>
    </TouchableOpacity>
  );

  const renderStory = (story: UserStory) => (
    <TouchableOpacity
      key={story.id}
      style={[
        styles.storyCard,
        { 
          backgroundColor: colors.cardBackground,
          shadowColor: colors.cardShadow,
          borderLeftColor: getStatusColor(story.status),
          borderLeftWidth: 4,
        }
      ]}
      onPress={() => handleStoryPress(story)}
    >
      <View style={styles.storyHeader}>
        <View style={styles.storyInfo}>
          <View style={styles.storyTitleRow}>
            <Text style={[styles.storyTitle, { color: colors.textPrimary }]}>
              {story.title}
            </Text>
            <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(story.priority) }]}>
              <Text style={[styles.priorityText, { color: colors.badgeText }]}>
                {story.priority}
              </Text>
            </View>
          </View>
          <Text style={[styles.storyDescription, { color: colors.textSecondary }]}>
            {story.description}
          </Text>
        </View>
      </View>

      <View style={styles.storyFooter}>
        <View style={styles.storyMeta}>
          <View style={styles.assigneeInfo}>
            <View style={[styles.avatar, { backgroundColor: colors.primary }]}>
              <Text style={[styles.avatarText, { color: colors.badgeText }]}>
                {story.assignedTo.avatar}
              </Text>
            </View>
            <View style={styles.assigneeDetails}>
              <Text style={[styles.assigneeName, { color: colors.textSecondary }]}>
                {story.assignedTo.name}
              </Text>
              <Text style={[styles.assigneeLabel, { color: colors.textTertiary }]}>
                Assigned to
              </Text>
            </View>
          </View>

          <View style={styles.storyPoints}>
            <Text style={[styles.pointsText, { color: colors.textSecondary }]}>
              {story.storyPoints} pts
            </Text>
          </View>
        </View>

        <View style={styles.storyStatus}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(story.status) }]}>
            <Text style={[styles.statusText, { color: colors.badgeText }]}>
              {story.status.replace('_', ' ')}
            </Text>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  const getFilterCounts = () => ({
    ALL: stories.length,
    TODO: stories.filter(s => s.status === 'TODO').length,
    IN_PROGRESS: stories.filter(s => s.status === 'IN_PROGRESS').length,
    TO_REVIEW: stories.filter(s => s.status === 'TO_REVIEW').length,
    DONE: stories.filter(s => s.status === 'DONE').length
  });

  const counts = getFilterCounts();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>User Stories</Text>
          <View style={styles.headerRight} />
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={[styles.filtersContainer, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.filterHeader}>
          <Ionicons name="filter" size={16} color={colors.iconSecondary} />
          <Text style={[styles.filterTitle, { color: colors.textPrimary }]}>Filter Stories</Text>
        </View>
        <View style={styles.filterSection}>
          <View style={styles.filterOptions}>
            {renderFilterTab('ALL', 'All', counts.ALL)}
            {renderFilterTab('TODO', 'To Do', counts.TODO)}
            {renderFilterTab('IN_PROGRESS', 'In Progress', counts.IN_PROGRESS)}
            {renderFilterTab('TO_REVIEW', 'To Review', counts.TO_REVIEW)}
            {renderFilterTab('DONE', 'Done', counts.DONE)}
          </View>
        </View>
      </View>

      {/* Stories List */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.storiesList}>
          {filteredStories.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="person" size={48} color={colors.textTertiary} />
              <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>No stories found</Text>
              <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
                Try changing your filter settings
              </Text>
            </View>
          ) : (
            filteredStories.map(renderStory)
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerRight: {
    width: 40,
  },
  filtersContainer: {
    padding: 16,
    borderBottomWidth: 1,
  },
  filterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  filterTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  filterSection: {
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterTab: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  filterTabText: {
    fontSize: 12,
    fontWeight: '500',
  },
  content: {
    flex: 1,
  },
  storiesList: {
    padding: 16,
  },
  storyCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  storyHeader: {
    marginBottom: 12,
  },
  storyInfo: {
    flex: 1,
  },
  storyTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  storyTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  storyDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  storyFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  storyMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  assigneeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  assigneeDetails: {
    flex: 1,
  },
  assigneeName: {
    fontSize: 12,
    fontWeight: '500',
  },
  assigneeLabel: {
    fontSize: 10,
  },
  storyPoints: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
  },
  pointsText: {
    fontSize: 12,
    fontWeight: '500',
  },
  storyStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
});
